import { HouseholdSchedule } from '../../../common/src/model.ts';
import { Pages } from '../../../common/src/pages.ts';
import { addListener, localStorageGet, LocalStorageKey, localStorageSet } from '../data/client_local_storage.ts';
import { getUrlForPage } from './path.ts';
import { notifyActiveListeners } from './use_is_app_active.tsx';

enum DayOfWeek {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

const householdPushNotificationTitle = 'Sing Your Part';

const sound = 'arpeggio.wav'; // can be either undefined or a sound file in the app bundle (e.g. 'arpeggio.wav')

const householdPushNotificationBody: { [key in DayOfWeek]: string } = {
  [DayOfWeek.Sunday]: "Happy Lord's day!",
  [DayOfWeek.Monday]: 'Check out the songs your church is singing this week!',
  [DayOfWeek.Tuesday]: "Let's sing your favorite song from last Sunday",
  [DayOfWeek.Wednesday]: 'Halfway through the week; start getting ready for Sunday',
  [DayOfWeek.Thursday]: "Three days away from church; make sure we're ready to sing",
  [DayOfWeek.Friday]: 'Finish the week strong; end it with singing!',
  [DayOfWeek.Saturday]: 'Are you ready to sing your part tomorrow?',
};

const householdPushNotificationTimes: { [key in HouseholdSchedule]: Pick<PushNotification, 'hour' | 'minute'> } = {
  [HouseholdSchedule.Morning]: { hour: 8, minute: 0 },
  [HouseholdSchedule.Lunch]: { hour: 11, minute: 30 },
  [HouseholdSchedule.Dinner]: { hour: 17, minute: 0 },
  [HouseholdSchedule.Bedtime]: { hour: 19, minute: 30 },
};

const householdPushNotificationIdentifierPrefix = 'household';

interface NativeInterface {
  arePushNotificationsAllowed(requestPermission: boolean): void;
  schedulePushNotification(notification: PushNotification): void;
  getPushNotifications(): void;
  cancelPushNotification(identifier: string): void;
}

export function deviceSupportsNotifications() {
  return Boolean(getNativeInterface()?.arePushNotificationsAllowed);
}

export enum PushNotificationAuthorization {
  Authorized = 'authorized',
  Denied = 'denied',
  NotDetermined = 'notDetermined',
}

// iOS app 4.17+ returns the answer via WKScriptMessageHandlerWithReply (postMessage returns
// a Promise). Older builds have no reply and instead push back via
// globalThis.receivePushNotificationsAuthorization. Register the legacy callback
// unconditionally so either path resolves the same promise.
// TODO(remove after 2027-01-01): once no old iOS apps are in the wild, simplify the body to
// `return (await nativeInterface.arePushNotificationsAllowed.postMessage({ requestPermission })).authorization`
// and drop the legacy globalThis.receivePushNotificationsAuthorization path.
export async function getPushNotificationsAuthorization({
  requestPermission,
}: {
  requestPermission: boolean;
}): Promise<PushNotificationAuthorization> {
  const nativeInterface = getNativeInterface();
  if (!nativeInterface?.arePushNotificationsAllowed) {
    console.error(`Failed to locate arePushNotificationsAllowed on window.webkit?.messageHandlers`);
    return PushNotificationAuthorization.Denied;
  }

  const previous = globalThis.receivePushNotificationsAuthorization;
  try {
    return await new Promise<PushNotificationAuthorization>((resolve) => {
      globalThis.receivePushNotificationsAuthorization = ({
        authorization,
      }: {
        authorization: PushNotificationAuthorization;
      }) => resolve(authorization);
      try {
        // @ts-expect-error - TODO(hewitt): This seems to be the incorrect TS signature
        const reply = nativeInterface.arePushNotificationsAllowed.postMessage({ requestPermission }) as
          Promise<{ authorization: PushNotificationAuthorization }> | undefined;
        if (reply && typeof reply.then === 'function') {
          void reply.then(({ authorization }) => resolve(authorization));
        }
      } catch {
        resolve(PushNotificationAuthorization.NotDetermined);
      }
    });
  } finally {
    globalThis.receivePushNotificationsAuthorization = previous;
  }
}

function getNativeInterface(): NativeInterface | undefined {
  return window.webkit?.messageHandlers as NativeInterface | undefined;
}

interface PushNotification {
  identifier: string;
  title: string;
  body: string;
  hour: number; // hour is in military time (e.g. 14 = 2pm)
  minute: number;
  recurring: boolean;
  weekday?: number; // 1 = Sunday and 7 = Saturday
  sound?: string; // name of sound file in iOS app bundle
}

async function setupAllHouseholdPushNotifications(): Promise<void> {
  await cancelAllHouseholdPushNotifications();
  const { schedule } = localStorageGet(LocalStorageKey.Household)?.surveyResponses ?? {};
  const nativeInterface = getNativeInterface();
  if (!schedule || !deviceSupportsNotifications()) {
    return;
  }
  for (const entry of schedule) {
    for (const notification of createPushNotificationsFromScheduleEntry(entry)) {
      // @ts-expect-error - TODO(hewitt): incorrect TS declaration?
      nativeInterface.schedulePushNotification.postMessage(notification);
    }
  }
}

function createPushNotificationsFromScheduleEntry(entry: HouseholdSchedule): PushNotification[] {
  return Object.values(DayOfWeek)
    .filter((value) => Number.isInteger(value))
    .map((dayOfWeek) => createPushNotification(entry, dayOfWeek as DayOfWeek));
}

export function createPushNotification(entry: HouseholdSchedule, dayOfWeek: DayOfWeek): PushNotification {
  const weekday = dayOfWeek + 1;
  // format: 'household-morning-2' (Monday morning identifier)
  const identifier = householdPushNotificationIdentifierPrefix + '-' + entry + '-' + weekday.toString();
  const title = householdPushNotificationTitle;
  const body = householdPushNotificationBody[dayOfWeek];
  const time = householdPushNotificationTimes[entry];
  return { identifier, title, body, ...time, recurring: true, weekday, sound: sound };
}

export async function refreshHouseholdPushNotifications(): Promise<void> {
  await cancelAllHouseholdPushNotifications();
  await setupAllHouseholdPushNotifications();
}

async function cancelAllHouseholdPushNotifications(): Promise<void> {
  for (const identifier of await getPushNotificationIdentifiers()) {
    if (identifier.startsWith(householdPushNotificationIdentifierPrefix)) {
      await cancelPushNotification(identifier);
    }
  }
}

// See getPushNotificationsAuthorization for the dual-path explanation.
// TODO(remove after 2027-01-01): once no old iOS apps are in the wild, simplify to
// `return (await nativeInterface.getPushNotifications.postMessage({})).identifiers` and drop
// the legacy globalThis.receivePushNotifications path.
async function getPushNotificationIdentifiers(): Promise<string[]> {
  const nativeInterface = getNativeInterface();
  if (!nativeInterface?.getPushNotifications) {
    console.error(`Unable to locate getPushNotifications on native interface`);
    return [];
  }

  const previous = globalThis.receivePushNotifications;
  try {
    return await new Promise<string[]>((resolve) => {
      globalThis.receivePushNotifications = ({ identifiers }: { identifiers: string[] }) => resolve(identifiers);
      // @ts-expect-error - TODO(hewitt): Incorrect TS declaration?
      const reply = nativeInterface.getPushNotifications.postMessage({}) as
        Promise<{ identifiers: string[] }> | undefined;
      if (reply && typeof reply.then === 'function') {
        void reply.then(({ identifiers }) => resolve(identifiers));
      }
    });
  } finally {
    globalThis.receivePushNotifications = previous;
  }
}

async function cancelPushNotification(identifier: string): Promise<void> {
  const nativeInterface = getNativeInterface();
  if (!nativeInterface?.cancelPushNotification) {
    console.error(`Unable to locate cancelPushNotification on native interface`);
    return;
  }
  // @ts-expect-error - TODO(hewitt): Incorrect TS declaration?
  nativeInterface.cancelPushNotification.postMessage({ identifier });
}

export function registerPushNotificationHandlers(navigate: (to: string) => Promise<void>) {
  registerForPushNotificationClicks(navigate);
  registerForHouseholdScheduleUpdates();
  registerForActiveSignal();
}

// Old iOS app builds push the tapped notification's identifier via
// globalThis.receiveOnPushNotificationClicked. New iOS builds (4.17+) navigate the webview
// to /today?minimize-player=1 instead, which is consumed by today_page.tsx via
// consumeMinimizePlayerQueryString. Keep this listener for backward compatibility.
// TODO(remove after 2027-01-01): once no old iOS apps are in the wild, delete this function
// and its call site in registerPushNotificationHandlers; the navigate parameter to
// registerPushNotificationHandlers/initializeApp can be dropped at the same time.
function registerForPushNotificationClicks(navigate: (to: string) => Promise<void>) {
  if (!deviceSupportsNotifications()) {
    return;
  }
  globalThis.receiveOnPushNotificationClicked = (identifier: string) => {
    console.log(`Clicked on notification ${identifier}`);
    localStorageSet(LocalStorageKey.IsPlayerMaximized, false);
    void navigate(getUrlForPage(Pages.Today));
  };
}

function registerForHouseholdScheduleUpdates() {
  if (!deviceSupportsNotifications()) {
    return;
  }
  let previousSchedule = localStorageGet(LocalStorageKey.Household)?.surveyResponses?.schedule;
  addListener(LocalStorageKey.Household, async () => {
    const schedule = localStorageGet(LocalStorageKey.Household)?.surveyResponses?.schedule;
    if (JSON.stringify(schedule) !== JSON.stringify(previousSchedule)) {
      console.log(`Household schedule update detected - refreshing push notifications`);
      await refreshHouseholdPushNotifications();
      previousSchedule = schedule;
    }
  });
}

// New iOS app builds (4.17+) no longer push receiveOnAppDidBecomeActive — Swift can't push
// reliably into the page under iOS 18 with limitsNavigationsToAppBoundDomains = true. We
// derive the signal from document.visibilitychange and pageshow, which fire on both browsers
// and the webview. We also keep the legacy push listener registered so older iOS app builds
// (which may foreground without firing the browser's visibility events reliably) still
// drive notifyActiveListeners. notifyActiveListeners short-circuits when state is unchanged,
// so the two paths do not conflict.
function registerForActiveSignal() {
  const handleActive = (active: boolean) => {
    notifyActiveListeners(active);
    if (active && deviceSupportsNotifications()) {
      const household = localStorageGet(LocalStorageKey.Household);
      if (household && household.surveyResponses) {
        void getPushNotificationsAuthorization({ requestPermission: true });
      }
    }
  };
  document.addEventListener('visibilitychange', () => handleActive(document.visibilityState === 'visible'));
  window.addEventListener('pageshow', () => handleActive(true));
  // TODO(remove after 2027-01-01): once no old iOS apps are in the wild, delete this legacy
  // push listener; the visibilitychange/pageshow listeners above cover new iOS builds.
  globalThis.receiveOnAppDidBecomeActive = (active?: boolean) => handleActive(active !== false);
}
